import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import {
  assetPaywallUpgradeModalOpenAtom,
  assetPaywallUpgradeModalPlanAtom,
} from '@/components/Modals/UpgradePlan/atoms';
import { useSubscribeModal } from '@/hooks/billing/useSubscribeModal';
import { useSubscriptionType } from '@/hooks/auth';
import { recordConversion } from '@/utils/analytics/conversion';

import type { ModalConversionPoints } from '@/components/Modals/shared-types';
import type { Plan } from '@/fixtures/plans';

interface OpenUpsellArgs {
  conversionPoint: ModalConversionPoints;
  targetPlan: Extract<Plan['name'], 'Creator' | 'Pro' | 'Business'>;
}

// Lower number = lower tier.
const PLAN_TIER: Record<Plan['name'], number> = {
  Free: 0,
  Essentials: 1,
  Creator: 2,
  Pro: 3,
  Business: 4,
};

/**
 * Picks the correct paywall modal for a *plan-tier* upsell:
 * - Free users get the Subscribe modal (initiates new Stripe checkout).
 * - Paying users on a lower tier get the Upgrade modal (in-app plan change).
 * - Users already at or above the target tier are a no-op (defensive — they
 *   shouldn't reach the paywall in the first place).
 *
 * Routing the wrong modal to existing subscribers makes Stripe reject the
 * checkout with `"Error in C..."` because they already have an active sub.
 *
 * Sibling of the Download Gateway (DEV-5841), not part of it. The gateway
 * owns the *asset-paywall* flow (writes the upsell atom via
 * `useAssetUpsellTrigger`); this hook is the *plan-tier* paywall used by
 * non-download surfaces (e.g. asset tile lock badges, the in-modal "Upgrade
 * to X" button). G5 may collapse the two if the boundary justifies it.
 */
export function useUpsellModal() {
  const subscriptionType = useSubscriptionType();
  const { openSubscribeModalWithConversion } = useSubscribeModal();
  const setUpgradeModalOpen = useSetAtom(assetPaywallUpgradeModalOpenAtom);
  const setUpgradeModalPlan = useSetAtom(assetPaywallUpgradeModalPlanAtom);

  const openUpsell = useCallback(
    ({ targetPlan, conversionPoint }: OpenUpsellArgs) => {
      if (subscriptionType === 'Free') {
        openSubscribeModalWithConversion(conversionPoint);
        return;
      }

      if (PLAN_TIER[subscriptionType] >= PLAN_TIER[targetPlan]) {
        return;
      }

      // Register the originating conversion point up-front so purchase events
      // are attributed to the paywall (e.g. DownloadUpsell), not the generic
      // UpgradeModal fallback that `useEnsureModalConversionPoint` registers.
      recordConversion({ source: conversionPoints[conversionPoint] });
      setUpgradeModalPlan(targetPlan);
      setUpgradeModalOpen(true);
    },
    [
      subscriptionType,
      openSubscribeModalWithConversion,
      setUpgradeModalOpen,
      setUpgradeModalPlan,
    ]
  );

  return { openUpsell };
}
